import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AuroraHeader, AuroraFooter } from "../components/aurora/Header";
import { MultiSelect, SingleSelect } from "../components/aurora/MultiSelect";
import { ShareButton } from "../components/aurora/ShareButton";
import {
  REGION_AND_COUNTRY_OPTIONS,
  LOCATION_OPTIONS,
  COUNTRY_SELECT_OPTIONS,
  COUNTRIES,
  INDUSTRIES,
  ROLES,
  COMPANY_SIZES,
  REVENUE_RANGES,
  ACTIVE_CONCERNS,
  PLANNING_HORIZONS,
} from "../lib/countries";
import { api } from "../lib/api";

const empty = {
  companyProfile: {
    companyName: "",
    role: "",
    roleOther: "",
    companyWebsite: "",
    industry: "",
    headquartersCountry: "",
    primaryMarkets: [],
    companySize: "",
    annualRevenue: "",
  },
  operationalExposure: {
    supplierLocations: [],
    assetLocations: [],
    tier23Visibility: "",
    sourcingVisibility: "",
    singleCountryDependency: "",
    singleCountryDependencyCountry: "",
    conflictAreaOps: "",
    conflictAreaCountries: [],
    regulatoryJurisdictions: [],
    govSoeCounterparty: "",
    govSoeCountries: [],
  },
  currentConcerns: {
    activeConcerns: [],
    activeConcernsOther: "",
    topPriorityRisks: [],
    disruptionExperienced: "",
    disruptionDescription: "",
    contingencyPlan: "",
    contingencyDescription: "",
    planningHorizon: "",
    concernCountry: "",
    concernCountryNote: "",
  },
  strategicContext: {
    decisionInforming: "",
    informingAreas: [],
    informingOther: "",
    additionalContext: "",
  },
  clientEmail: "",
};

const SECTIONS = ["Company Profile", "Operational Exposure", "Current Concerns", "Strategic Context"];

const Field = ({ label, helper, children }) => (
  <div className="intake-field">
    <div className="label-eyebrow">{label}</div>
    {helper && <div className="intake-helper" data-testid="field-helper">{helper}</div>}
    <div className={helper ? "mt-3" : "mt-2"}>{children}</div>
  </div>
);

const SectionHeading = ({ children }) => (
  <h3 className="intake-section-heading">{children}</h3>
);

const YES_NO_NS = ["Yes", "No", "Not sure"];

const YesNoRadio = ({ name, value, onChange, testId }) => (
  <div className="flex gap-6 mt-2" data-testid={testId}>
    {YES_NO_NS.map((v) => (
      <label key={v} className="flex items-center gap-2 cursor-pointer font-body">
        <input
          type="radio"
          name={name}
          value={v}
          checked={value === v}
          onChange={() => onChange(v)}
          className="aurora-check"
          data-testid={`${testId}-${v.toLowerCase().replace(" ", "-")}`}
        />
        <span className="text-[#190F08]">{v}</span>
      </label>
    ))}
  </div>
);

const INFORMING_OPTIONS = [
  "Risk analysis",
  "Stakeholder engagement",
  "Market entry advisory",
  "Scenario planning",
  "Crisis response",
  "Regulatory compliance",
  "Supply chain restructuring",
  "Other",
];

const stepCellColor = (active, done) => {
  if (active) return "#FAF5EE";
  if (done) return "#A88059";
  return "#6B5E4A";
};

const StepIndicator = ({ step }) => (
  <div className="flex items-center gap-4 mb-12" data-testid="step-indicator">
    {SECTIONS.map((s, i) => {
      const n = i + 1;
      const active = n === step;
      const done = n < step;
      return (
        <div key={s} className="flex items-center gap-3 flex-1">
          <div
            className="font-heading text-sm flex items-center justify-center w-9 h-9 border"
            style={{
              borderColor: active || done ? "#A88059" : "rgba(168,128,89,0.3)",
              color: stepCellColor(active, done),
              background: active ? "#A88059" : "transparent",
            }}
            data-testid={`step-number-${n}`}
          >
            {n}
          </div>
          <div className="hidden md:block">
            <div className="label-eyebrow" style={{ color: active ? "#6B4D2B" : "#6B5E4A" }}>
              {s}
            </div>
          </div>
          {n < SECTIONS.length && <div className="hidden md:block flex-1 h-px bg-[#A88059]/30" />}
        </div>
      );
    })}
  </div>
);

export default function IntakeForm() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [data, setData] = useState(empty);
  const [submitting, setSubmitting] = useState(false);

  const update = (section, patch) =>
    setData((d) => ({ ...d, [section]: { ...d[section], ...patch } }));

  const validateStep = () => {
    if (step === 1) {
      const cp = data.companyProfile;
      if (!cp.companyName || !cp.role || !cp.industry || !cp.headquartersCountry || !cp.companySize) {
        toast.error("Please complete required fields");
        return false;
      }
      if (cp.role === "Other" && !cp.roleOther.trim()) {
        toast.error("Please specify your role");
        return false;
      }
      if (cp.primaryMarkets.length === 0) {
        toast.error("Please specify at least one primary market");
        return false;
      }
    }
    if (step === 2) {
      const op = data.operationalExposure;
      if (op.supplierLocations.length === 0) {
        toast.error("Please specify key sourcing locations (or select N/A)");
        return false;
      }
      if (!op.tier23Visibility) {
        toast.error("Please answer the tier-2/3 supplier visibility question");
        return false;
      }
      if (!op.sourcingVisibility) {
        toast.error("Please answer the original-source visibility question");
        return false;
      }
      if (op.assetLocations.length === 0) {
        toast.error("Please specify key physical asset locations (or select N/A)");
        return false;
      }
      if (!op.singleCountryDependency) {
        toast.error("Please answer the single-country dependency question");
        return false;
      }
      if (op.singleCountryDependency === "Yes" && !op.singleCountryDependencyCountry) {
        toast.error("Please specify which country");
        return false;
      }
      if (!op.conflictAreaOps) {
        toast.error("Please answer the conflict-area operations question");
        return false;
      }
      if (op.conflictAreaOps === "Yes" && op.conflictAreaCountries.length === 0) {
        toast.error("Please specify which conflict-area countries");
        return false;
      }
      if (op.regulatoryJurisdictions.length === 0) {
        toast.error("Please select at least one regulatory jurisdiction");
        return false;
      }
      if (!op.govSoeCounterparty) {
        toast.error("Please answer the government / SOE counterparty question");
        return false;
      }
      if (op.govSoeCounterparty === "Yes" && op.govSoeCountries.length === 0) {
        toast.error("Please specify which countries");
        return false;
      }
    }
    if (step === 3) {
      const cc = data.currentConcerns;
      if (cc.activeConcerns.length === 0) {
        toast.error("Please select at least one active geopolitical concern");
        return false;
      }
      if (cc.activeConcerns.includes("Other") && !cc.activeConcernsOther) {
        toast.error("Please describe the other concern");
        return false;
      }
      if (!cc.disruptionExperienced) {
        toast.error("Please answer the geopolitical disruption question");
        return false;
      }
      if (!cc.contingencyPlan) {
        toast.error("Please answer the contingency plan question");
        return false;
      }
      if (!cc.planningHorizon) {
        toast.error("Please select a strategic planning horizon");
        return false;
      }
    }
    if (step === 4) {
      const sc = data.strategicContext;
      if (sc.informingAreas.length === 0) {
        toast.error("Please select at least one area this assessment is informing");
        return false;
      }
      if (sc.informingAreas.includes("Other") && !sc.informingOther) {
        toast.error("Please describe the other area");
        return false;
      }
      const email = (data.clientEmail || "").trim();
      // Basic RFC-lite check — one @, at least one dot in the domain
      if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
        toast.error("Please enter a valid email address");
        return false;
      }
    }
    return true;
  };

  const next = () => {
    if (!validateStep()) return;
    setStep((s) => Math.min(4, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const prev = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const res = await api.createSubmission(data);
      const id = res.data.submissionId;
      // Replace the intake form's history entry so browser back from the
      // results page doesn't re-mount this component with wiped state.
      // The submission is already persisted in Mongo under `id`; the client
      // can bookmark /submission/{id} or use browser forward to return.
      navigate(`/submission/${id}`, { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col intake-form">
      <AuroraHeader />
      <main className="flex-1 max-w-4xl w-full mx-auto px-6 sm:px-10 py-12 sm:py-16">
        {step > 1 && (
          <div className="mb-6">
            <button
              type="button"
              onClick={prev}
              className="btn-ghost inline-flex items-center"
              data-testid="btn-prev-top"
              disabled={submitting}
            >
              ← Previous
            </button>
          </div>
        )}
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="label-eyebrow mb-3 text-[#6B4D2B]" data-testid="intake-eyebrow">
              Confidential Intake
            </div>
            <h2 className="font-heading-bold text-4xl sm:text-5xl text-[#190F08]">
              Geopolitical Exposure Assessment
            </h2>
          </div>
          {step === 1 && (
            <ShareButton
              testId="btn-share-intake"
              title="Aurora Insights — Geopolitical Exposure Assessment"
              text="Take a 5-minute confidential intake with Aurora Insights."
              label="Share"
            />
          )}
        </div>
        <p className="font-body text-[#190F08] max-w-2xl leading-relaxed mb-10 mt-4">
          This structured assessment identifies the precise geopolitical risks material to your
          business operations. Every response is strictly confidential and will be reviewed by a
          senior Aurora Insights analyst.
        </p>

        <hr className="gold-rule mb-10" />

        <StepIndicator step={step} />        {/* STEP 1 — Company Profile */}
        {step === 1 && (
          <div className="step-enter" data-testid="step-1">
            <SectionHeading>Company Profile</SectionHeading>

            <Field label="Company name *">
              <input
                className="aurora-input"
                value={data.companyProfile.companyName}
                onChange={(e) => update("companyProfile", { companyName: e.target.value })}
                placeholder="e.g. Pacific Logistics Holdings"
                data-testid="input-company-name"
              />
            </Field>

            <Field label="Your role / function *">
              <SingleSelect
                testId="select-role"
                options={ROLES}
                value={data.companyProfile.role}
                onChange={(v) =>
                  update("companyProfile", {
                    role: v,
                    roleOther: v === "Other" ? data.companyProfile.roleOther : "",
                  })
                }
                placeholder="Select your role"
              />
              {data.companyProfile.role === "Other" && (
                <div className="mt-4">
                  <input
                    className="aurora-input"
                    value={data.companyProfile.roleOther}
                    onChange={(e) => update("companyProfile", { roleOther: e.target.value })}
                    placeholder="Please specify your role"
                    data-testid="input-role-other"
                  />
                </div>
              )}
            </Field>

            <Field label="Company website">
              <input
                className="aurora-input"
                value={data.companyProfile.companyWebsite}
                onChange={(e) => update("companyProfile", { companyWebsite: e.target.value })}
                placeholder="https://example.com"
                data-testid="input-company-website"
              />
            </Field>

            <div className="grid md:grid-cols-2 gap-8">
              <Field label="Industry / sector *">
                <SingleSelect
                  testId="select-industry"
                  options={INDUSTRIES}
                  value={data.companyProfile.industry}
                  onChange={(v) => update("companyProfile", { industry: v })}
                  placeholder="Select industry"
                />
              </Field>

              <Field label="Headquarters country *">
                <SingleSelect
                  testId="select-hq"
                  options={COUNTRY_SELECT_OPTIONS}
                  value={data.companyProfile.headquartersCountry}
                  onChange={(v) => update("companyProfile", { headquartersCountry: v })}
                  placeholder="Select country"
                />
              </Field>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <Field label="Primary Market Locations *">
                <MultiSelect
                  testId="multi-primary-markets"
                  options={REGION_AND_COUNTRY_OPTIONS}
                  selected={data.companyProfile.primaryMarkets}
                  onChange={(v) => update("companyProfile", { primaryMarkets: v })}
                  placeholder="Add regions or countries"
                />
              </Field>
              <div />
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <Field label="Number of employees *">
                <SingleSelect
                  testId="select-size"
                  options={COMPANY_SIZES}
                  value={data.companyProfile.companySize}
                  onChange={(v) => update("companyProfile", { companySize: v })}
                  placeholder="Select size"
                />
              </Field>

              <Field label="Annual revenue (optional)">
                <SingleSelect
                  testId="select-revenue"
                  options={REVENUE_RANGES}
                  value={data.companyProfile.annualRevenue}
                  onChange={(v) => update("companyProfile", { annualRevenue: v })}
                  placeholder="Select range"
                />
              </Field>
            </div>
          </div>
        )}

        {/* STEP 2 — Operational Exposure */}
        {step === 2 && (
          <div className="step-enter" data-testid="step-2">
            <SectionHeading>Operational Exposure</SectionHeading>

            <Field label="Key sourcing locations *" helper="you may select more than one">
              <MultiSelect
                testId="multi-suppliers"
                options={LOCATION_OPTIONS}
                selected={data.operationalExposure.supplierLocations}
                onChange={(v) => update("operationalExposure", { supplierLocations: v })}
                placeholder="Add sourcing locations"
              />
            </Field>

            <Field label="Do you know who your tier-2 and tier-3 suppliers are? *">
              <YesNoRadio
                name="tier23"
                testId="radio-tier23"
                value={data.operationalExposure.tier23Visibility}
                onChange={(v) => update("operationalExposure", { tier23Visibility: v })}
              />
            </Field>

            <Field label="Do you know where your key materials, components, or products are originally sourced from? *">
              <YesNoRadio
                name="sourceVis"
                testId="radio-source-visibility"
                value={data.operationalExposure.sourcingVisibility}
                onChange={(v) => update("operationalExposure", { sourcingVisibility: v })}
              />
            </Field>

            <Field label="Key physical assets / infrastructure *" helper="you may select more than one">
              <MultiSelect
                testId="multi-assets"
                options={LOCATION_OPTIONS}
                selected={data.operationalExposure.assetLocations}
                onChange={(v) => update("operationalExposure", { assetLocations: v })}
                placeholder="Add asset locations"
              />
            </Field>

            <Field label="Single-country dependency > 30% of supply or revenue *">
              <YesNoRadio
                name="singleDep"
                testId="radio-single-dep"
                value={data.operationalExposure.singleCountryDependency}
                onChange={(v) =>
                  update("operationalExposure", {
                    singleCountryDependency: v,
                    singleCountryDependencyCountry: v === "No" ? "" : data.operationalExposure.singleCountryDependencyCountry,
                  })
                }
              />
              {data.operationalExposure.singleCountryDependency === "Yes" && (
                <div className="mt-4">
                  <SingleSelect
                    testId="select-single-dep-country"
                    options={COUNTRY_SELECT_OPTIONS}
                    value={data.operationalExposure.singleCountryDependencyCountry}
                    onChange={(v) =>
                      update("operationalExposure", { singleCountryDependencyCountry: v })
                    }
                    placeholder="Which country?"
                    searchable
                  />
                </div>
              )}
            </Field>

            <Field label="Operations in conflict-prone or unstable government areas *">
              <YesNoRadio
                name="conflictOps"
                testId="radio-conflict"
                value={data.operationalExposure.conflictAreaOps}
                onChange={(v) =>
                  update("operationalExposure", {
                    conflictAreaOps: v,
                    conflictAreaCountries: v === "Yes" ? data.operationalExposure.conflictAreaCountries : [],
                  })
                }
              />
              {data.operationalExposure.conflictAreaOps === "Yes" && (
                <div className="mt-4">
                  <MultiSelect
                    testId="multi-conflict-countries"
                    options={COUNTRY_SELECT_OPTIONS}
                    selected={data.operationalExposure.conflictAreaCountries}
                    onChange={(v) => update("operationalExposure", { conflictAreaCountries: v })}
                    placeholder="Which countries?"
                  />
                </div>
              )}
            </Field>

            <Field label="Regulatory jurisdiction exposure *">
              <MultiSelect
                testId="multi-regulatory"
                options={COUNTRIES}
                selected={data.operationalExposure.regulatoryJurisdictions}
                onChange={(v) => update("operationalExposure", { regulatoryJurisdictions: v })}
                placeholder="Select jurisdictions"
              />
            </Field>

            <Field label="Do any of your key suppliers, customers, or partners include government agencies or state-owned enterprises? *">
              <YesNoRadio
                name="govSoe"
                testId="radio-gov-soe"
                value={data.operationalExposure.govSoeCounterparty}
                onChange={(v) =>
                  update("operationalExposure", {
                    govSoeCounterparty: v,
                    govSoeCountries: v === "Yes" ? data.operationalExposure.govSoeCountries : [],
                  })
                }
              />
              {data.operationalExposure.govSoeCounterparty === "Yes" && (
                <div className="mt-4">
                  <MultiSelect
                    testId="multi-gov-soe-countries"
                    options={COUNTRY_SELECT_OPTIONS}
                    selected={data.operationalExposure.govSoeCountries}
                    onChange={(v) => update("operationalExposure", { govSoeCountries: v })}
                    placeholder="In which countries?"
                  />
                </div>
              )}
            </Field>
          </div>
        )}

        {/* STEP 3 — Current Concerns */}
        {step === 3 && (
          <div className="step-enter" data-testid="step-3">
            <SectionHeading>Current Concerns</SectionHeading>

            <Field label="Active geopolitical concerns *">
              <div className="grid sm:grid-cols-2 gap-3 mt-2" data-testid="checklist-concerns">
                {ACTIVE_CONCERNS.map((c) => {
                  const sel = data.currentConcerns.activeConcerns.includes(c);
                  return (
                    <label key={c} className="flex items-start gap-3 cursor-pointer font-body py-1">
                      <input
                        type="checkbox"
                        className="aurora-check mt-1"
                        checked={sel}
                        onChange={() => {
                          const list = sel
                            ? data.currentConcerns.activeConcerns.filter((x) => x !== c)
                            : [...data.currentConcerns.activeConcerns, c];
                          // Drop any top priority no longer in the concern list
                          const priorities = data.currentConcerns.topPriorityRisks.filter((p) =>
                            list.includes(p)
                          );
                          update("currentConcerns", {
                            activeConcerns: list,
                            topPriorityRisks: priorities,
                          });
                        }}
                        data-testid={`check-concern-${c.slice(0, 20)}`}
                      />
                      <span className="text-[#190F08] text-sm leading-snug">{c}</span>
                    </label>
                  );
                })}
              </div>
              {data.currentConcerns.activeConcerns.includes("Other") && (
                <div className="mt-4">
                  <input
                    className="aurora-input"
                    value={data.currentConcerns.activeConcernsOther}
                    onChange={(e) =>
                      update("currentConcerns", { activeConcernsOther: e.target.value })
                    }
                    placeholder="Describe other concern"
                    data-testid="input-concern-other"
                  />
                </div>
              )}
            </Field>

            {data.currentConcerns.activeConcerns.length > 1 && (
              <Field
                label="From the risks you selected, which are your top 1–2 priorities?"
                helper="Select the risks that concern you most right now."
              >
                <div className="grid sm:grid-cols-2 gap-3 mt-2" data-testid="checklist-priorities">
                  {data.currentConcerns.activeConcerns.map((c) => {
                    const sel = data.currentConcerns.topPriorityRisks.includes(c);
                    const atCap = data.currentConcerns.topPriorityRisks.length >= 2 && !sel;
                    return (
                      <label
                        key={c}
                        className={`flex items-start gap-3 font-body py-1 ${
                          atCap ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="aurora-check mt-1"
                          checked={sel}
                          disabled={atCap}
                          onChange={() => {
                            const next = sel
                              ? data.currentConcerns.topPriorityRisks.filter((x) => x !== c)
                              : [...data.currentConcerns.topPriorityRisks, c];
                            update("currentConcerns", { topPriorityRisks: next });
                          }}
                          data-testid={`check-priority-${c.slice(0, 20)}`}
                        />
                        <span className="text-[#190F08] text-sm leading-snug">{c}</span>
                      </label>
                    );
                  })}
                </div>
              </Field>
            )}

            <Field label="Geopolitical disruption experienced in last 12 months *">
              <YesNoRadio
                name="disruption"
                testId="radio-disruption"
                value={data.currentConcerns.disruptionExperienced}
                onChange={(v) => update("currentConcerns", { disruptionExperienced: v })}
              />
              <textarea
                className="aurora-input mt-4"
                value={data.currentConcerns.disruptionDescription}
                onChange={(e) =>
                  update("currentConcerns", { disruptionDescription: e.target.value })
                }
                placeholder="Brief description (optional)"
                data-testid="textarea-disruption"
              />
            </Field>

            <Field label="Contingency plan if disruption occurs *">
              <YesNoRadio
                name="contingency"
                testId="radio-contingency"
                value={data.currentConcerns.contingencyPlan}
                onChange={(v) => update("currentConcerns", { contingencyPlan: v })}
              />
              <textarea
                className="aurora-input mt-4"
                value={data.currentConcerns.contingencyDescription}
                onChange={(e) =>
                  update("currentConcerns", { contingencyDescription: e.target.value })
                }
                placeholder="Brief description (optional)"
                data-testid="textarea-contingency"
              />
            </Field>

            <Field label="Strategic planning horizon *">
              <SingleSelect
                testId="select-horizon"
                options={PLANNING_HORIZONS}
                value={data.currentConcerns.planningHorizon}
                onChange={(v) => update("currentConcerns", { planningHorizon: v })}
                placeholder="Select horizon"
              />
            </Field>

            <Field
              label="Is there a specific country or market you are particularly concerned about? (Optional)"
              helper="This may be a market where you have significant exposure, or one where the political or regulatory situation is deteriorating."
            >
              <SingleSelect
                testId="select-concern-country"
                options={COUNTRY_SELECT_OPTIONS}
                value={data.currentConcerns.concernCountry}
                onChange={(v) =>
                  update("currentConcerns", {
                    concernCountry: v,
                    concernCountryNote:
                      v === "" ? "" : data.currentConcerns.concernCountryNote,
                  })
                }
                placeholder="Select a country or market"
              />
              {data.currentConcerns.concernCountry && (
                <div className="mt-4">
                  <div className="intake-helper mb-2">Briefly, what is your concern?</div>
                  <input
                    className="aurora-input"
                    value={data.currentConcerns.concernCountryNote}
                    onChange={(e) => {
                      const v = e.target.value.slice(0, 150);
                      update("currentConcerns", { concernCountryNote: v });
                    }}
                    placeholder="Up to 150 characters"
                    maxLength={150}
                    data-testid="input-concern-country-note"
                  />
                  <div className="font-body text-xs text-[#6B5E4A] mt-1 text-right">
                    {data.currentConcerns.concernCountryNote.length}/150
                  </div>
                </div>
              )}
            </Field>
          </div>
        )}

        {/* STEP 4 — Strategic Context */}
        {step === 4 && (
          <div className="step-enter" data-testid="step-4">
            <SectionHeading>Strategic Context</SectionHeading>

            <Field label="What is your primary goal in engaging us? *" helper="Select all that apply">
              <div className="grid sm:grid-cols-2 gap-3 mt-2" data-testid="checklist-informing">
                {INFORMING_OPTIONS.map((c) => {
                  const sel = data.strategicContext.informingAreas.includes(c);
                  return (
                    <label key={c} className="flex items-start gap-3 cursor-pointer font-body py-1">
                      <input
                        type="checkbox"
                        className="aurora-check mt-1"
                        checked={sel}
                        onChange={() => {
                          const list = sel
                            ? data.strategicContext.informingAreas.filter((x) => x !== c)
                            : [...data.strategicContext.informingAreas, c];
                          update("strategicContext", { informingAreas: list });
                        }}
                        data-testid={`check-informing-${c.slice(0, 16)}`}
                      />
                      <span className="text-[#190F08] text-sm leading-snug">{c}</span>
                    </label>
                  );
                })}
              </div>
              {data.strategicContext.informingAreas.includes("Other") && (
                <div className="mt-4">
                  <textarea
                    className="aurora-input"
                    value={data.strategicContext.informingOther}
                    onChange={(e) =>
                      update("strategicContext", { informingOther: e.target.value })
                    }
                    placeholder="Describe the other decision or area"
                    data-testid="textarea-informing-other"
                  />
                </div>
              )}
            </Field>

            <Field label="Anything else Aurora should know (optional)">
              <textarea
                className="aurora-input"
                value={data.strategicContext.additionalContext}
                onChange={(e) =>
                  update("strategicContext", { additionalContext: e.target.value })
                }
                placeholder="Context, constraints, sensitivities..."
                data-testid="textarea-additional"
              />
            </Field>

            <Field
              label="Your email address *"
              helper="Aurora will use this to follow up with your full report."
            >
              <input
                type="email"
                className="aurora-input"
                value={data.clientEmail}
                onChange={(e) =>
                  setData((d) => ({ ...d, clientEmail: e.target.value }))
                }
                placeholder="you@company.com"
                required
                data-testid="input-client-email"
              />
            </Field>
          </div>
        )}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={prev}
            disabled={step === 1 || submitting}
            className="btn-ghost"
            data-testid="btn-prev"
          >
            ← Previous
          </button>

          {step < 4 ? (
            <button
              type="button"
              onClick={next}
              className="btn-primary"
              data-testid="btn-next"
            >
              Continue →
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              className="btn-primary"
              data-testid="btn-submit"
            >
              {submitting ? "Generating Assessment..." : "Submit for Assessment"}
            </button>
          )}
        </div>

        {submitting && (
          <div className="mt-8 text-center label-eyebrow text-[#6B4D2B]" data-testid="submitting-loader">
            Aurora is analysing your profile · this takes ~20 seconds
          </div>
        )}
      </main>
      <AuroraFooter />
    </div>
  );
}
